/**
 * AdminDashboard — vue d'ensemble (Phase A-1).
 * 4 KPI cards + table des derniers événements d'audit.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, FolderKanban, Activity, AlertTriangle, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchAdminStats, fetchRecentAudit } from "@/lib/admin-api";

function KpiCard({ label, value, icon: Icon, accent = "default", testId }) {
  const tones = {
    default: "border-border bg-card",
    danger: "border-red-300 bg-red-50/60 dark:bg-red-950/10",
    warn: "border-amber-300 bg-amber-50/60 dark:bg-amber-950/10",
  };
  return (
    <div
      className={`rounded-xl border p-4 shadow-sm transition ${tones[accent] || tones.default}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-2 font-mono text-3xl font-bold tabular-nums">
        {value === null || value === undefined ? "—" : value.toLocaleString("fr-FR")}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (!status) return <Badge variant="outline">—</Badge>;
  if (status >= 500) return <Badge className="bg-red-700 text-white">{status}</Badge>;
  if (status >= 400) return <Badge className="bg-amber-600 text-white">{status}</Badge>;
  if (status >= 200 && status < 300) return <Badge className="bg-emerald-600 text-white">{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([fetchAdminStats(), fetchRecentAudit(20)]);
      setStats(s);
      setEvents(e);
    } catch (err) {
      toast.error("Impossible de charger les statistiques admin.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000); // refresh 30 s
    return () => clearInterval(id);
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tableau de bord administrateur</h1>
          <p className="text-sm text-muted-foreground">
            Vue d'ensemble en temps réel — rafraîchi toutes les 30 secondes.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-refresh-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Utilisateurs totaux"
          value={stats?.total_users}
          icon={Users}
          testId="kpi-total-users"
        />
        <KpiCard
          label="Actifs aujourd'hui"
          value={stats?.active_users_today}
          icon={Users}
          testId="kpi-active-today"
        />
        <KpiCard
          label="Sessions actives"
          value={stats?.active_sessions}
          icon={Activity}
          testId="kpi-active-sessions"
        />
        <KpiCard
          label="Alertes sécurité"
          value={stats?.unresolved_security_events}
          icon={AlertTriangle}
          accent={stats?.unresolved_security_events > 0 ? "danger" : "default"}
          testId="kpi-security-alerts"
        />
        <KpiCard
          label="Appels LLM / jour"
          value={stats?.llm_calls_today}
          icon={Activity}
          testId="kpi-llm-calls"
        />
        <KpiCard
          label="Stockage DB (Mo)"
          value={stats?.db_storage_mb}
          icon={FolderKanban}
          testId="kpi-storage"
        />
      </div>

      {stats?.failed_auth_last_24h > 0 && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/70 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/20"
          data-testid="kpi-failed-auth-alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-700 dark:text-amber-400" />
          <div>
            <strong>{stats.failed_auth_last_24h}</strong> tentative(s) d'authentification refusée(s)
            sur les 24 dernières heures. Consultez le journal d'audit pour les détails.
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activité récente — 20 derniers événements</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="recent-events-table">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2">Horodatage</th>
                <th className="px-4 py-2">Utilisateur</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">IP</th>
                <th className="px-4 py-2">Statut</th>
                <th className="px-4 py-2">Latence</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    Aucun événement à afficher.
                  </td>
                </tr>
              )}
              {events.map((e, i) => (
                <tr
                  key={i}
                  className={`border-b last:border-b-0 ${e.critical ? "bg-red-50/50 dark:bg-red-950/10" : ""}`}
                  data-testid={`event-row-${i}`}
                >
                  <td className="px-4 py-1.5 font-mono text-xs">
                    {e.created_at ? new Date(e.created_at).toLocaleTimeString("fr-FR") : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-xs">
                    {e.user_email || <span className="italic text-muted-foreground">anonyme</span>}
                  </td>
                  <td className="px-4 py-1.5 font-mono text-xs">
                    {e.critical && <span className="mr-1 text-red-700 dark:text-red-400">●</span>}
                    {e.action}
                  </td>
                  <td className="px-4 py-1.5 font-mono text-[11px]">{e.ip || "—"}</td>
                  <td className="px-4 py-1.5">
                    <StatusBadge status={e.status} />
                  </td>
                  <td className="px-4 py-1.5 font-mono text-xs">
                    {e.latency_ms ? `${Math.round(e.latency_ms)} ms` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
